import api from "@/services/api";
import authService from "@/services/auth";

const APP_CODE = "mail";
const PROMPT_KEY = "mail_push_prompted_v1";

let configCache = null;
let serviceWorkerRegistration = null;
let syncPromise = null;

function hasBrowserSupport() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

function shouldManagePush() {
  const user = authService.getCurrentUser();
  return Boolean(user && !user.is_super_admin && authService.isAuthenticated());
}

function markPrompted() {
  try {
    window.localStorage.setItem(PROMPT_KEY, "1");
  } catch (error) {
    // ignore storage restrictions
  }
}

function hasPrompted() {
  try {
    return window.localStorage.getItem(PROMPT_KEY) === "1";
  } catch (error) {
    return false;
  }
}

function base64UrlToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray;
}

async function fetchConfig() {
  if (configCache) {
    return configCache;
  }

  const response = await api.get("/v1/saas/push/config", {
    params: { app_code: APP_CODE },
  });

  configCache = response.data?.data ?? response.data ?? {};
  return configCache;
}

async function registerServiceWorker(path) {
  if (serviceWorkerRegistration) {
    return serviceWorkerRegistration;
  }

  serviceWorkerRegistration = await navigator.serviceWorker.register(path, {
    scope: "/",
  });

  return serviceWorkerRegistration;
}

async function requestPermissionIfNeeded(promptIfNeeded) {
  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission === "denied" || !promptIfNeeded || hasPrompted()) {
    return false;
  }

  markPrompted();
  const permission = await Notification.requestPermission();
  return permission === "granted";
}

async function syncSubscription(promptIfNeeded = false) {
  if (!hasBrowserSupport() || !shouldManagePush()) {
    return false;
  }

  const config = await fetchConfig();
  if (!config?.enabled || !config?.vapid_public_key) {
    return false;
  }

  const granted = await requestPermissionIfNeeded(promptIfNeeded);
  if (!granted && Notification.permission !== "granted") {
    return false;
  }

  const registration = await registerServiceWorker(config.service_worker_path || "/mail-push-sw.js");
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(config.vapid_public_key),
    });
  }

  await api.post("/v1/saas/push/subscriptions", {
    app_code: APP_CODE,
    device_label: window.navigator.platform || "Browser",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    platform: window.navigator.userAgentData?.platform || window.navigator.platform || "",
    origin: window.location.origin,
    subscription: subscription.toJSON(),
  });

  return true;
}

export async function ensureMailPushSubscription({ promptIfNeeded = false } = {}) {
  if (syncPromise) {
    return syncPromise;
  }

  syncPromise = syncSubscription(promptIfNeeded)
    .catch(() => false)
    .finally(() => {
      syncPromise = null;
    });

  return syncPromise;
}

export function initMailPushNotifications(router) {
  if (!hasBrowserSupport()) {
    return;
  }

  const maybeRegister = (route) => {
    if (!route?.meta?.requiresCustomer) {
      return;
    }

    const path = String(route.path || "");
    if (!path.startsWith("/mail")) {
      return;
    }

    window.setTimeout(() => {
      ensureMailPushSubscription({ promptIfNeeded: false });
    }, 0);
  };

  maybeRegister(router.currentRoute.value);
  router.afterEach((to) => {
    maybeRegister(to);
  });
}
